/**
 * The two things you can do with a Luma answer's decorations.
 *
 * An answer carries `reference` (the documents Luma actually read) and `filter`
 * (a suggested search, inferred from the exchange) independently — nearly every
 * answer has both. They used to be handled as an either/or, which meant the
 * suggested search was discarded whenever references existed, i.e. almost
 * always. These are the two matching actions, offered side by side.
 *
 * Both actions carry a count, so the size of what's behind them is visible
 * before clicking. References are countable on the spot; the suggested search
 * has to be run to be counted, so it is resolved once and reused for both the
 * count and the navigation.
 */

import type { MeasureDocumentAdapter } from '@/adapters/measure-document-adapter';
import type { ApalumaChatMessage } from '@/types/api-response/chat-session-types';
import type { DocumentFilter } from '@/types/measure-document-types';
import { ref } from 'vue';
import { createMeasureDocumentAdapter } from '@/adapters/measure-document-adapter';
import { useDocumentSchema } from '@/composables/use-document-schema';
import { useQueryNavigation } from '@/composables/use-query-navigation';
import { useScreenWidth } from '@/composables/use-screen-width';
import { useNmedDocAiUiStore } from '@/stores/nmed/nmed-doc-ai-ui-store';
import { useNmedDocSearchFilterStore } from '@/stores/nmed/nmed-doc-search-filter-store';
import {
   keepIndexedFilterValues,
   lumaFilterKeywords,
   mapLumaFiltersToDocumentFilters,
} from '@/utils/luma-filters';

/* ---------------------------------------------------------------------------------------------- */

/**
 * Field value lists, shared for the session.
 *
 * Every answer in a thread suggests much the same handful of fields, so without
 * this each message's count would refetch the same vocabularies. Module scope
 * rather than component scope for that reason; tests reset it with
 * `vi.resetModules()`.
 */
const vocabularyCache = new Map<string, Promise<string[] | null>>();

/** A suggested search, resolved into something the search page can run. */
export interface ResolvedSuggestedSearch {
   filters: DocumentFilter[];
   keywords: string;
}

/* ---------------------------------------------------------------------------------------------- */

export function useLumaAnswerActions(
   adapter: MeasureDocumentAdapter = createMeasureDocumentAdapter(),
) {
   const docSearchFilterStore = useNmedDocSearchFilterStore();
   const docAiUiStore = useNmedDocAiUiStore();
   const { isSmScreen } = useScreenWidth();
   const { goToSearch } = useQueryNavigation();
   const { schemaFields, loadSchema } = useDocumentSchema();

   /** True while the schema needed to translate a suggested search is loading. */
   const isOpeningSearch = ref(false);
   /** How many documents the suggested search returns; null until known. */
   const searchResultCount = ref<number | null>(null);
   const isCountingResults = ref(false);

   // One resolution per component instance — the count computes it, the click reuses it.
   let resolution: Promise<ResolvedSuggestedSearch> | null = null;

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Put this answer's references in the document table beside the chat.
    *
    * On a small screen that panel is hidden, so the sheet is opened instead.
    */
   function showReferences(message: ApalumaChatMessage) {
      if (!message.reference?.length)
         return;

      docSearchFilterStore.addLumaDocs(message.id.toString(), message.reference, message.thread_id);

      if (isSmScreen.value)
         docAiUiStore.openMobileDocsSheet();
   }

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Turn Luma's suggestions into a runnable search.
    *
    * Two rounds of validation, because Luma names fields better than it names
    * values. The schema says how a field is addressed; the facet vocabulary
    * says whether the value exists. A suggestion that survives both becomes a
    * facet. Anything else becomes a keyword only if *nothing* survived — see
    * the note on `keywords` for why a leftover can't be added alongside facets.
    */
   function resolveSuggestedSearch(message: ApalumaChatMessage): Promise<ResolvedSuggestedSearch> {
      resolution ??= (async () => {
         const suggested = message.filter ?? [];

         await loadSchema();
         const { filters, unmapped } = mapLumaFiltersToDocumentFilters(suggested, schemaFields.value);

         const { kept, rejected } = filters.length
            ? keepIndexedFilterValues(filters, await fetchFieldVocabularies(filters))
            : { kept: [], rejected: [] };

         const leftovers = [
            ...unmapped.map(key => valueForKey(suggested, key)),
            ...rejected.map(f => String(f.value)),
         ].filter(Boolean);

         // Keywords are a *fallback*, not a supplement. The backend ANDs the
         // keyword with the facets, and a leftover is a metadata value the index
         // doesn't offer as a facet — not evidence about document text. Adding it
         // alongside surviving facets can only narrow toward zero: Luma suggested
         // a good `Kirtland Air Force Base` facet plus three long-tail SubType
         // values, and searching those as text turned 2,641 documents into 0.
         //
         // So when anything survived as a facet, the leftovers are dropped. That
         // gives a search broader than Luma suggested rather than an empty one —
         // and the surviving facets still describe the answer.
         const keywords = kept.length
            ? ''
            : leftovers.join(' ') || lumaFilterKeywords(suggested);

         return { filters: kept, keywords };
      })();

      return resolution;
   }

   /**
    * Count what the suggested search would return.
    *
    * Costs a search — there is no way to know without running one — so it asks
    * for a single row and reads the total off the pagination. Left as `null` on
    * failure so the control shows no count rather than a wrong one.
    */
   async function loadSuggestedSearchCount(message: ApalumaChatMessage) {
      if (!message.filter?.length)
         return;

      isCountingResults.value = true;
      try {
         const { filters, keywords } = await resolveSuggestedSearch(message);
         const response = await adapter.search({
            keyword: keywords,
            filters,
            page: 1,
            per_page: 1,
         });
         searchResultCount.value = response.meta?.total ?? null;
      }
      catch (error) {
         console.error('Could not count the suggested search:', error);
         searchResultCount.value = null;
      }
      finally {
         isCountingResults.value = false;
      }
   }

   /* ------------------------------------------------------------------------------------------- */

   /** Open the search results Luma's suggested filters describe. */
   async function openSuggestedSearch(message: ApalumaChatMessage) {
      if (!message.filter?.length)
         return;

      isOpeningSearch.value = true;
      try {
         const { filters, keywords } = await resolveSuggestedSearch(message);
         if (filters.length || keywords)
            await goToSearch(keywords, { filters });
      }
      catch (error) {
         // A template click handler returns this promise, so a rejection here
         // surfaces as an unhandled rejection rather than anything the user or
         // the UI can act on. The button leaving its loading state is the
         // feedback; the reason belongs in the console.
         console.error('Could not open the suggested search:', error);
      }
      finally {
         isOpeningSearch.value = false;
      }
   }

   /**
    * Values the index actually holds, per field.
    *
    * One request per distinct field, in parallel and cached for the session. A
    * field whose lookup fails is left out of the map, which
    * `keepIndexedFilterValues` reads as "unknown, apply optimistically".
    */
   async function fetchFieldVocabularies(
      filters: readonly DocumentFilter[],
   ): Promise<Map<string, string[]>> {
      const fields = [...new Set(filters.map(f => `${f.group}.${f.code}`))];

      const entries = await Promise.all(fields.map(async (field) => {
         let pending = vocabularyCache.get(field);
         if (!pending) {
            pending = adapter.searchFacets({ field })
               .then(facet => facet.buckets.map(b => b.value))
               .catch((error) => {
                  console.error(`Could not read the value list for ${field}:`, error);
                  // Don't cache a failure — the next answer should try again.
                  vocabularyCache.delete(field);
                  return null;
               });
            vocabularyCache.set(field, pending);
         }

         const values = await pending;
         return values === null ? null : ([field, values] as const);
      }));

      return new Map(entries.filter((e): e is NonNullable<typeof e> => e !== null));
   }

   return {
      isOpeningSearch,
      searchResultCount,
      isCountingResults,
      showReferences,
      openSuggestedSearch,
      loadSuggestedSearchCount,
   };
}

/** The value Luma paired with a given key, for keyword fallback. */
function valueForKey(suggested: readonly Record<string, unknown>[], key: string): string {
   for (const entry of suggested) {
      if (key in entry) {
         const raw = entry[key];
         if (typeof raw === 'string' || typeof raw === 'number')
            return String(raw).trim();
      }
   }
   return '';
}
